import { Editor, Extension } from '@tiptap/core';
import { Plugin, PluginKey, EditorState } from '@tiptap/pm/state';
import { Decoration, DecorationSet, EditorView } from '@tiptap/pm/view';
import { Fragment, Node as ProseMirrorNode, Slice } from '@tiptap/pm/model';
import FileHandler from '@tiptap/extension-file-handler';

import { AttachmentImageInsertPayload } from './phase1-image';

/** 10 MB per dropped image — same limit as the attachments panel and the server. */
export const IMAGE_DROP_MAX_BYTES = 10 * 1024 * 1024;
export const IMAGE_DROP_MAX_SIZE_MESSAGE = 'Maximum file size exceeded';
export const IMAGE_DROP_MIME_TYPES = ['image/png', 'image/jpeg'];
const IMAGE_DROP_EXTENSIONS = ['.png', '.jpg', '.jpeg'];

/** Returns the error message for a file that may not be dropped into the editor, or null if it's fine. */
export function validateDroppedImage(file: File): string | null {
  const type = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();
  // Some platforms hand over an empty MIME type for dragged files — fall back to the extension then.
  const supported = type
    ? IMAGE_DROP_MIME_TYPES.includes(type)
    : IMAGE_DROP_EXTENSIONS.some((ext) => name.endsWith(ext));
  if (!supported) {
    return `"${file.name}" is not a supported image type. Use PNG or JPEG.`;
  }
  if (file.size > IMAGE_DROP_MAX_BYTES) {
    return IMAGE_DROP_MAX_SIZE_MESSAGE;
  }
  return null;
}

export interface ImageDropHandlers {
  /** Stores the file as an attachment of the note; resolves with what insertAttachmentImage needs. */
  upload: (file: File) => Promise<AttachmentImageInsertPayload>;
  /** Called once per image actually inserted into the document. */
  onInserted?: (payload: AttachmentImageInsertPayload) => void;
  onError: (message: string) => void;
}

// ---------------------------------------------------------------------------
// Inline "Inserting image…" placeholder: a widget decoration that marks where the
// pending images go and follows edits made while they upload.
// ---------------------------------------------------------------------------

interface PlaceholderMeta {
  set?: { id: string; pos: number; label: string };
  remove?: { id: string };
}

export const imageDropPlaceholderKey = new PluginKey<DecorationSet>('imageDropPlaceholder');

const createPlaceholderElement = (label: string): HTMLElement => {
  const el = document.createElement('span');
  el.className = 'tiptap-image-drop-placeholder';
  el.contentEditable = 'false';
  el.setAttribute('role', 'status');
  const icon = document.createElement('span');
  icon.className = 'pi pi-spin pi-spinner';
  icon.setAttribute('aria-hidden', 'true');
  const text = document.createElement('span');
  text.textContent = label;
  el.append(icon, text);
  return el;
};

const findPlaceholderPos = (state: EditorState, id: string): number | null => {
  const set = imageDropPlaceholderKey.getState(state);
  const found = set?.find(undefined, undefined, (spec) => spec['id'] === id) ?? [];
  return found.length ? found[0].from : null;
};

const ImageDropPlaceholderPlugin = new Plugin<DecorationSet>({
  key: imageDropPlaceholderKey,
  state: {
    init: () => DecorationSet.empty,
    apply(tr, set) {
      let next = set.map(tr.mapping, tr.doc);
      const meta = tr.getMeta(imageDropPlaceholderKey) as PlaceholderMeta | undefined;
      const id = meta?.set?.id ?? meta?.remove?.id;
      if (id) {
        next = next.remove(next.find(undefined, undefined, (spec) => spec['id'] === id));
      }
      if (meta?.set) {
        const { pos, label } = meta.set;
        const widget = Decoration.widget(pos, () => createPlaceholderElement(label), {
          id,
          key: `${id}:${label}`,
          side: -1,
        });
        next = next.add(tr.doc, [widget]);
      }
      return next;
    },
  },
  props: {
    decorations(state) {
      return imageDropPlaceholderKey.getState(state);
    },
  },
});

let dropSequence = 0;

const setPlaceholder = (editor: Editor, meta: PlaceholderMeta): void => {
  if (editor.isDestroyed) {
    return;
  }
  const tr = editor.state.tr.setMeta(imageDropPlaceholderKey, meta).setMeta('addToHistory', false);
  editor.view.dispatch(tr);
};

/** Position right after the image node referencing attachmentId, or null if it's not in the doc. */
const findPosAfterImage = (editor: Editor, attachmentId: string): number | null => {
  let result: number | null = null;
  editor.state.doc.descendants((node, pos) => {
    if (result !== null) {
      return false;
    }
    if (node.type.name === 'image' && node.attrs['attachmentId'] === attachmentId) {
      result = pos + node.nodeSize;
      return false;
    }
    return true;
  });
  return result;
};

/**
 * Validates the dropped files, stores the valid ones as attachments (all uploads run in parallel)
 * and inserts them at the drop position in their original order, whatever order the uploads
 * finish in: image N goes in only once images 1..N-1 have been inserted or have failed. A failed
 * file is reported and skipped, so no reference to a missing attachment is ever inserted.
 */
export async function insertDroppedImages(
  editor: Editor,
  files: File[],
  pos: number,
  handlers: ImageDropHandlers,
): Promise<void> {
  const valid: File[] = [];
  const errors: string[] = [];
  for (const file of files) {
    const error = validateDroppedImage(file);
    if (error) {
      if (!errors.includes(error)) {
        errors.push(error);
      }
    } else {
      valid.push(file);
    }
  }
  errors.forEach((message) => handlers.onError(message));
  if (valid.length === 0) {
    return;
  }

  const id = `image-drop-${++dropSequence}`;
  const total = valid.length;
  const labelFor = (done: number) => (total === 1 ? 'Inserting image…' : `Inserting images… (${done}/${total})`);
  const startPos = Math.max(0, Math.min(pos, editor.state.doc.content.size));
  setPlaceholder(editor, { set: { id, pos: startPos, label: labelFor(0) } });

  // undefined = still uploading, null = failed (skipped), otherwise ready to insert.
  const results: Array<AttachmentImageInsertPayload | null | undefined> = new Array(total).fill(undefined);
  let nextIndex = 0;

  const flush = (): void => {
    while (nextIndex < total && results[nextIndex] !== undefined) {
      const payload = results[nextIndex++];
      if (!payload || editor.isDestroyed) {
        continue;
      }
      // The placeholder follows edits made during the upload; if its spot was deleted, fall back to the cursor.
      const at = findPlaceholderPos(editor.state, id) ?? editor.state.selection.to;
      const inserted = editor.commands.insertAttachmentImage(payload, at);
      if (inserted) {
        handlers.onInserted?.(payload);
        // Next image goes right after this one (insertContentAt may have split the paragraph or
        // placed the block after it, so look the node up instead of mapping the old position).
        const after = findPosAfterImage(editor, payload.attachmentId);
        if (after !== null && nextIndex < total) {
          setPlaceholder(editor, { set: { id, pos: after, label: labelFor(nextIndex) } });
        }
      }
    }
    if (nextIndex >= total) {
      setPlaceholder(editor, { remove: { id } });
    } else if (!editor.isDestroyed) {
      const current = findPlaceholderPos(editor.state, id);
      if (current !== null) {
        setPlaceholder(editor, { set: { id, pos: current, label: labelFor(nextIndex) } });
      }
    }
  };

  await Promise.all(
    valid.map(async (file, index) => {
      try {
        const payload = await handlers.upload(file);
        results[index] = payload?.attachmentId ? payload : null;
        if (!payload?.attachmentId) {
          handlers.onError(`Couldn't insert "${file.name}".`);
        }
      } catch (err) {
        results[index] = null;
        const detail = err instanceof Error && err.message ? err.message : 'upload failed';
        handlers.onError(detail === IMAGE_DROP_MAX_SIZE_MESSAGE ? detail : `Couldn't insert "${file.name}": ${detail}`);
      }
      flush();
    }),
  );
}

// ---------------------------------------------------------------------------
// Paste
// ---------------------------------------------------------------------------

/** True when pasted HTML carries no text — e.g. the browser's "Copy image", which puts the image file
 * plus an `<img src="…">` on the clipboard. Such a paste is handled as the image file itself. */
export function isImageOnlyHtml(html: string | null | undefined): boolean {
  const source = String(html ?? '').trim();
  if (!source) {
    return true;
  }
  const template = document.createElement('template');
  template.innerHTML = source;
  return (template.content.textContent ?? '').trim() === '';
}

const isEmbeddedSrc = (src: string) => /^(data|blob):/i.test(src);
const isWebSrc = (src: string) => /^https?:\/\//i.test(src);

/**
 * Gives pasted images without a source (plain `<img src>` from another page or app) the same
 * metadata as images inserted through the editor: web images become 'linked' (like Insert Image >
 * From URL); embedded ones (data:/blob: URLs) are collected so they can be stored as attachments.
 */
export function classifyPastedImages(slice: Slice, embedded: Set<string>): Slice {
  const mapFragment = (fragment: Fragment): Fragment => {
    const children: ProseMirrorNode[] = [];
    fragment.forEach((node) => {
      if (node.type.name === 'image' && !node.attrs['imageSourceType'] && !node.attrs['attachmentId']) {
        const src = String(node.attrs['src'] ?? '').trim();
        if (isWebSrc(src)) {
          children.push(node.type.create({ ...node.attrs, imageSourceType: 'linked', originalUrl: src }, node.content, node.marks));
          return;
        }
        if (isEmbeddedSrc(src)) {
          embedded.add(src);
        }
        children.push(node);
        return;
      }
      children.push(node.content.size ? node.copy(mapFragment(node.content)) : node);
    });
    return Fragment.fromArray(children);
  };
  return new Slice(mapFragment(slice.content), slice.openStart, slice.openEnd);
}

/**
 * Pasted from outside the editor (a web page, another app), an image's width/height are the source's
 * layout — often a small thumbnail size — not the picture's: drop them so it shows at its native size
 * (still capped at the editor's width). Copied from a note (ProseMirror marks its HTML with
 * data-pm-slice), it keeps the size it was given there.
 */
export function stripForeignImageSizes(html: string): string {
  if (!/<img\b/i.test(html) || /data-pm-slice/i.test(html)) {
    return html;
  }
  const template = document.createElement('template');
  template.innerHTML = html;
  const images = template.content.querySelectorAll('img');
  images.forEach((img) => {
    img.removeAttribute('width');
    img.removeAttribute('height');
    img.style.removeProperty('width');
    img.style.removeProperty('height');
    if (!img.getAttribute('style')?.trim()) {
      img.removeAttribute('style');
    }
  });
  return template.innerHTML;
}

let pastedImageSequence = 0;

const fileFromEmbeddedSrc = async (src: string): Promise<File> => {
  const blob = await (await fetch(src)).blob();
  const type = blob.type || 'application/octet-stream';
  const ext = type === 'image/jpeg' ? 'jpg' : (type.split('/')[1] || 'bin').replace(/[^a-z0-9]/gi, '');
  const stamp = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14);
  return new File([blob], `pasted-image-${stamp}-${++pastedImageSequence}.${ext}`, { type });
};

/** Positions of image nodes still showing this embedded source (not yet turned into an attachment). */
const findEmbeddedImages = (view: EditorView, src: string): Array<{ pos: number; node: ProseMirrorNode }> => {
  const found: Array<{ pos: number; node: ProseMirrorNode }> = [];
  view.state.doc.descendants((node, pos) => {
    if (node.type.name === 'image' && node.attrs['src'] === src && !node.attrs['attachmentId']) {
      found.push({ pos, node });
    }
  });
  return found;
};

/**
 * Stores an embedded pasted image as a note attachment and repoints its node(s) at it, so no base64
 * stays in the note. If it can't be stored (unsupported type, too big, upload error) the image is
 * removed again and the error reported — never left as an embedded copy outside the attachments.
 */
export async function storeEmbeddedImage(editor: Editor, src: string, handlers: ImageDropHandlers): Promise<void> {
  let error: string | null = null;
  let payload: AttachmentImageInsertPayload | null = null;
  try {
    const file = await fileFromEmbeddedSrc(src);
    error = validateDroppedImage(file);
    if (!error) {
      payload = await handlers.upload(file);
    }
  } catch (err) {
    error = err instanceof Error && err.message ? err.message : 'upload failed';
    if (error !== IMAGE_DROP_MAX_SIZE_MESSAGE) {
      error = `Couldn't store the pasted image: ${error}`;
    }
  }
  if (editor.isDestroyed) {
    return;
  }

  const view = editor.view;
  const targets = findEmbeddedImages(view, src);
  if (targets.length === 0) {
    return;
  }
  const tr = view.state.tr;
  for (const { pos, node } of targets.reverse()) {
    if (payload?.attachmentId) {
      tr.setNodeMarkup(pos, undefined, {
        ...node.attrs,
        src: payload.src ?? payload.url,
        imageSourceType: 'attachment',
        attachmentId: payload.attachmentId,
        originalUrl: null,
      });
    } else {
      tr.delete(pos, pos + node.nodeSize);
    }
  }
  // Part of the paste as far as the user is concerned — don't make it a separate undo step.
  view.dispatch(tr.setMeta('addToHistory', false));
  if (payload?.attachmentId) {
    handlers.onInserted?.(payload);
  } else {
    handlers.onError(error ?? "Couldn't store the pasted image.");
  }
}

const imagePasteKey = new PluginKey('imagePaste');

export interface ImageDropOptions {
  /** Supplied by the host component; null = image drops are not handled (e.g. no note to attach to). */
  getHandlers: () => ImageDropHandlers | null;
}

/**
 * Drag & drop of PNG/JPEG files into the editor, built on Tiptap's FileHandler. Dropped images are
 * stored as note attachments (by the host's upload handler) and inserted as regular attachment
 * image nodes (Phase1Image), so they behave exactly like images inserted from the Insert Image dialog.
 * Drops without files (text, internal node moves) are left to the editor's default handling.
 */
export const ImageDrop = Extension.create<ImageDropOptions>({
  name: 'imageDrop',

  addOptions() {
    return {
      getHandlers: () => null,
    };
  },

  addExtensions() {
    const getHandlers = () => this.options.getHandlers();
    return [
      FileHandler.configure({
        // No allowedMimeTypes: unsupported files must reach onDrop so they can be reported
        // (FileHandler would otherwise silently fall back to the browser opening the file).
        onDrop: (editor: Editor, files: File[], pos: number) => {
          const handlers = getHandlers();
          if (!editor.isEditable || !handlers) {
            return;
          }
          void insertDroppedImages(editor, files, pos, handlers);
        },
      }),
    ];
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    const getHandlers = () => this.options.getHandlers();
    const embedded = new Set<string>();

    const pastePlugin = new Plugin({
      key: imagePasteKey,
      props: {
        // Image files on the clipboard (screenshot, the browser's "Copy image"): store them as
        // attachments and insert them at the cursor, exactly like a drop.
        handlePaste(view, event) {
          const files = Array.from(event.clipboardData?.files ?? []);
          if (!view.editable || files.length === 0 || !isImageOnlyHtml(event.clipboardData?.getData('text/html'))) {
            return false; // no files, or text with images: the normal paste (plus transformPasted) handles it
          }
          event.preventDefault();
          const handlers = getHandlers();
          if (!handlers) {
            return true;
          }
          if (!view.state.selection.empty) {
            editor.commands.deleteSelection();
          }
          void insertDroppedImages(editor, files, editor.state.selection.from, handlers);
          return true;
        },
        transformPastedHTML(html) {
          return stripForeignImageSizes(html);
        },
        transformPasted(slice) {
          return classifyPastedImages(slice, embedded);
        },
      },
      view: () => ({
        update: () => {
          if (embedded.size === 0) {
            return;
          }
          const sources = [...embedded];
          embedded.clear();
          const handlers = getHandlers();
          for (const src of sources) {
            if (handlers) {
              void storeEmbeddedImage(editor, src, handlers);
            }
          }
        },
      }),
    });

    return [ImageDropPlaceholderPlugin, pastePlugin];
  },
});
